import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Divider,
} from "@mui/material";
import {
  BugReport,
  Lightbulb,
  ArrowBack,
  Send,
  Person,
  Computer,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { setAdminFeedback } from "../../../redux/actions/admin";
import useAdminAPIClient from "../../../api/admin/hooks/useAdminAPIClient";
import dayjs from "dayjs";

const FeedbackView = ({
  feedbackId,
  feedbackType,
  onBack,
  onSwitchToMessaging,
}) => {
  const [replyMessage, setReplyMessage] = useState("");
  const [sending, setSending] = useState(false);
  const dispatch = useDispatch();
  const { feedback = [], users = [] } = useSelector((state) => state.admin);
  const { getFeedback, markFeedbackAsRead, sendMessageToUser } =
    useAdminAPIClient();

  const item = feedback.find(
    (f) => f.feedbackId?.toString() === feedbackId?.toString(),
  );
  const user = users.find((u) => u.userId === item?.userId);
  const displayName =
    user?.displayName ||
    user?.name ||
    user?.email?.split("@")[0] ||
    item?.userId?.substring(0, 8) + "...";

  // Mark as read when opened
  useEffect(() => {
    if (item && !item.read) {
      const markAsRead = async () => {
        await markFeedbackAsRead([item.feedbackId?.toString()]);
        const data = await getFeedback();
        if (data?.feedback) {
          dispatch(setAdminFeedback(data.feedback));
        }
      };
      markAsRead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackId]);

  const handleSendReply = async () => {
    if (!replyMessage.trim() || sending || !item?.userId) return;

    setSending(true);
    try {
      const subject =
        feedbackType === "bug-report"
          ? "Re: Your Bug Report"
          : "Re: Your Feature Request";

      await sendMessageToUser({
        userId: item.userId,
        message: replyMessage.trim(),
        subject,
      });
      setReplyMessage("");
      // Switch to messaging/conversations tab to show the conversation
      if (onSwitchToMessaging) {
        onSwitchToMessaging(item.userId);
      }
    } catch (error) {
      console.error("Failed to send reply:", error);
    } finally {
      setSending(false);
    }
  };

  if (!item) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          p: 3,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          Select a{" "}
          {feedbackType === "bug-report" ? "bug report" : "feature request"} to
          view
        </Typography>
      </Box>
    );
  }

  const Icon = feedbackType === "bug-report" ? BugReport : Lightbulb;
  const iconColor = feedbackType === "bug-report" ? "#d32f2f" : "#813773";
  const title =
    feedbackType === "bug-report" ? "Bug Report" : "Feature Request";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          bgcolor: iconColor,
          color: "white",
          borderRadius: "8px 8px 0 0",
        }}
      >
        {onBack && (
          <IconButton onClick={onBack} sx={{ color: "white" }}>
            <ArrowBack />
          </IconButton>
        )}
        <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)" }}>
          <Icon />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: "bold",
              mb: "0 !important",
              color: "white !important",
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="caption"
            sx={{ opacity: 0.9, mb: "0 !important", color: "white !important" }}
          >
            from {displayName} •{" "}
            {dayjs(item.createdAt).format("DD MMM YYYY, HH:mm")}
          </Typography>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: 3 }} className="scroller">
        {/* User Info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="overline" color="text.secondary">
            User Information
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <Person sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography variant="body2" sx={{ mb: "0 !important" }}>
              <strong>User ID:</strong> {item.userId}
            </Typography>
          </Box>
          {user?.email && (
            <Typography variant="body2" sx={{ ml: 3.5, mb: "0 !important" }}>
              <strong>Email:</strong> {user.email}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Bug Report Details */}
        {feedbackType === "bug-report" && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="overline" color="text.secondary">
                What Happened
              </Typography>
              <Typography
                variant="body1"
                sx={{ mt: 1, whiteSpace: "pre-wrap" }}
              >
                {item.whatHappened}
              </Typography>
            </Box>

            {item.whereWereYou && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="overline" color="text.secondary">
                  Where Were They
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  {item.whereWereYou}
                </Typography>
              </Box>
            )}

            {item.whatPlatform && (
              <Box
                sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}
              >
                <Computer sx={{ color: "text.secondary" }} />
                <Typography variant="body2" sx={{ mb: "0 !important" }}>
                  <strong>Platform:</strong> {item.whatPlatform}
                </Typography>
              </Box>
            )}

            <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
              {item.didRefreshFix ? (
                <CheckCircle sx={{ color: "success.main" }} />
              ) : (
                <Cancel sx={{ color: "error.main" }} />
              )}
              <Typography variant="body2" sx={{ mb: "0 !important" }}>
                <strong>Refresh fixed it:</strong>{" "}
                {item.didRefreshFix ? "Yes" : "No"}
              </Typography>
            </Box>

            {item.otherComments && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="overline" color="text.secondary">
                  Other Comments
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mt: 1, whiteSpace: "pre-wrap" }}
                >
                  {item.otherComments}
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* Feature Request Details */}
        {feedbackType === "feature-request" && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="overline" color="text.secondary">
                Feature Description
              </Typography>
              <Typography
                variant="body1"
                sx={{ mt: 1, whiteSpace: "pre-wrap" }}
              >
                {item.featureDescription}
              </Typography>
            </Box>

            {item.featureBenefit && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="overline" color="text.secondary">
                  Expected Benefit
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mt: 1, whiteSpace: "pre-wrap" }}
                >
                  {item.featureBenefit}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Reply Section */}
      <Box
        sx={{
          p: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "#fafafa",
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Reply to User
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            fullWidth
            placeholder="Type your response..."
            value={replyMessage}
            onChange={(e) => setReplyMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendReply();
              }
            }}
            multiline
            maxRows={3}
            size="small"
            disabled={sending}
          />
          <IconButton
            onClick={handleSendReply}
            disabled={!replyMessage.trim() || sending}
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              bgcolor: "#197b68",
              color: "white !important",
              "&:hover": { bgcolor: "#004a3c" },
              "&:disabled": {
                bgcolor: "grey.300",
                color: "grey.500 !important",
              },
              "& svg": { color: "white !important" },
            }}
          >
            <Send sx={{ color: "white !important" }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default FeedbackView;
